import { useRef, useState, useEffect, useCallback } from 'react';
import { timingsUserCredentialsQuery } from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import { convertMsToSeconds } from '@/shared/lib/date';
import { useGraphqlClient } from '@/shared/hooks/useGraphqlClient';
import { useInstanceCallback } from '@/scenarios/legacy-player/hooks/useInstanceCallback';
import { PlayingState } from '@/scenarios/legacy-player/types/instance';
import { useOnCurrentTimeChanged } from '@/scenarios/legacy-player/hooks/useOnCurrentTimeChanged';

import { sendTimings } from '../utils/send-timings';

const DEFAULT_SAVE_TIMINGS_INTERVAL = 20000;
const UNAUTHORIZED_STATUS_CODE = 401;

type SaveTimingsParams = {
    contentId: string;
    tvSeriesId?: string;
    videoToken?: string;
    timingsInterval?: number;
};

type UserCredentials = {
    authorizationToken?: string;
    profileId?: number;
};

function useUserInfo(onRequestCompleted?: () => void) {
    const client = useGraphqlClient();
    const [credentials, setCredentials] = useState<UserCredentials>({});
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchCredentials = useCallback(async () => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const { data } = await client.fetch(
                { operation: timingsUserCredentialsQuery },
                { abortSignal: controller.signal }
            );
            if (controller.signal.aborted) return;
            setCredentials({
                authorizationToken: data?.timingsAuthToken ?? undefined,
                profileId: data?.profileId ?? undefined,
            });
        } catch {
            // ignore: empty credentials disable timings submission
        } finally {
            if (!controller.signal.aborted) {
                onRequestCompleted?.();
            }
        }
    }, [client, onRequestCompleted]);

    useEffect(() => {
        fetchCredentials();
        return () => {
            abortControllerRef.current?.abort();
        };
    }, [fetchCredentials]);

    return {
        authorizationToken: credentials.authorizationToken,
        profileId: credentials.profileId,
        refetchToken: fetchCredentials,
    };
}

export function useSaveTimings({
    contentId,
    tvSeriesId,
    videoToken,
    timingsInterval = DEFAULT_SAVE_TIMINGS_INTERVAL,
}: SaveTimingsParams) {
    const isTimingsSavingAvailable = useRef(false);
    const lastSentTime = useRef<number | undefined>();

    const onUserInfoLoaded = useCallback(() => {
        isTimingsSavingAvailable.current = true;
    }, []);
    const { authorizationToken, profileId, refetchToken } = useUserInfo(onUserInfoLoaded);

    const handleCurrentTimeChanged = useInstanceCallback(
        (instance, currentTime: number) => {
            const isTimeIntervalEnded =
                lastSentTime.current === undefined ||
                Math.abs(currentTime - lastSentTime.current) > convertMsToSeconds(timingsInterval);

            if (
                profileId &&
                authorizationToken &&
                isTimingsSavingAvailable.current &&
                isTimeIntervalEnded &&
                videoToken !== undefined &&
                instance?.playingState !== PlayingState.end
            ) {
                lastSentTime.current = currentTime;

                sendTimings({
                    contentGroupUuid: contentId,
                    profileId,
                    videoToken,
                    time: currentTime,
                    serialContentGroupUuid: tvSeriesId,
                    authorizationToken,
                })?.then((response) => {
                    if (response.status === UNAUTHORIZED_STATUS_CODE) {
                        isTimingsSavingAvailable.current = false;
                        refetchToken();
                    }
                });
            }
        },
        [
            timingsInterval,
            profileId,
            videoToken,
            authorizationToken,
            contentId,
            tvSeriesId,
            refetchToken,
        ]
    );

    useOnCurrentTimeChanged(handleCurrentTimeChanged);
}
